import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PlusIcon, Trash2Icon, CameraIcon, SendIcon, AlertCircleIcon, MessageCircleIcon } from 'lucide-react';
import { NoticeBar } from './NoticeBar';
import { Reveal } from './Reveal';
const WHATSAPP_NUMBER = '2349030977339';
const markets = ['Eko Market', 'Balogun Market', 'Mile 12', 'Any / You decide'];
type ListItem = {
  id: number;
  name: string;
  quantity: string;
  budget: string;
};
type Tab = 'type' | 'photo';
type FieldKey = 'name' | 'phone' | 'market' | 'list' | 'address';
type Errors = Partial<Record<FieldKey, string>>;
let nextId = 4;
function createItem(): ListItem {
  nextId += 1;
  return {
    id: nextId,
    name: '',
    quantity: '',
    budget: ''
  };
}
const inputBase = 'w-full rounded-2xl border bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/35 outline-none transition-all duration-200';
const inputClass = `${inputBase} border-lavender focus:border-purple focus:ring-4 focus:ring-purple/20`;
const inputErrorClass = `${inputBase} border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-200`;
const labelClass = 'block text-sm font-bold text-purple';
function isValidNigerianPhone(value: string) {
  const cleaned = value.replace(/[\s()-]/g, '');
  return /^0\d{10}$/.test(cleaned) || /^(\+?234)\d{10}$/.test(cleaned);
}
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} role="alert" className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-red-600" data-id="element-118" data-mp="3komjrav8ahuu">
      <AlertCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-119" data-mp="3pnhbx3f35irm" />
      {message}
    </p>;
}
export function OrderForm() {
  const [tab, setTab] = useState<Tab>('type');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [market, setMarket] = useState('');
  const [items, setItems] = useState<ListItem[]>([{
    id: 1,
    name: '',
    quantity: '',
    budget: ''
  }, {
    id: 2,
    name: '',
    quantity: '',
    budget: ''
  }, {
    id: 3,
    name: '',
    quantity: '',
    budget: ''
  }]);
  const [overallBudget, setOverallBudget] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryDay, setDeliveryDay] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [showPhotoToast, setShowPhotoToast] = useState(false);
  const reduceMotion = useReducedMotion();
  const fieldRefs = {
    name: useRef<HTMLInputElement>(null),
    phone: useRef<HTMLInputElement>(null),
    market: useRef<HTMLSelectElement>(null),
    list: useRef<HTMLFieldSetElement>(null),
    address: useRef<HTMLTextAreaElement>(null)
  };
  useEffect(() => {
    return () => {
      setShowPhotoToast(false);
    };
  }, []);
  function updateItem(id: number, field: keyof Omit<ListItem, 'id'>, value: string) {
    setItems(current => current.map(item => item.id === id ? {
      ...item,
      [field]: value
    } : item));
    if (errors.list) setErrors(current => ({
      ...current,
      list: undefined
    }));
  }
  function removeItem(id: number) {
    setItems(current => current.length > 1 ? current.filter(item => item.id !== id) : current);
  }
  function clearError(field: FieldKey) {
    setErrors(current => current[field] ? {
      ...current,
      [field]: undefined
    } : current);
  }
  function validate(): Errors {
    const next: Errors = {};
    if (!name.trim()) {
      next.name = 'Please tell me your name so I know who I am shopping for.';
    }
    if (!phone.trim()) {
      next.phone = 'Please add your phone number so I can reach you about your order.';
    } else if (!isValidNigerianPhone(phone)) {
      next.phone = 'That number does not look right. Use 11 digits like 08030000000, or +2348030000000.';
    }
    if (!market) {
      next.market = 'Please choose a market, or pick "Any / You decide" and I will advise.';
    }
    if (tab === 'type' && items.every(item => !item.name.trim())) {
      next.list = 'Please add at least one item, or switch to the photo option.';
    }
    if (!address.trim()) {
      next.address = 'Please add your delivery address so we know where to bring your items.';
    }
    return next;
  }
  function buildWhatsAppUrl() {
    const lines: string[] = ['*New Market Run — Swift Errands*', ''];
    lines.push(`*Name:* ${name.trim()}`);
    lines.push(`*Phone / WhatsApp:* ${phone.trim()}`);
    lines.push(`*Market:* ${market}`);
    lines.push('');
    if (tab === 'type') {
      const filled = items.filter(item => item.name.trim().length > 0);
      lines.push('*Shopping list:*');
      filled.forEach((item, index) => {
        const quantity = item.quantity.trim() ? ` — Qty: ${item.quantity.trim()}` : '';
        const budget = item.budget.trim() ? ` — Budget: ₦${item.budget.trim()}` : '';
        lines.push(`${index + 1}. ${item.name.trim()}${quantity}${budget}`);
      });
    } else {
      lines.push('*Shopping list:* Photo of my written list');
    }
    lines.push('');
    lines.push(`*Overall budget:* ${overallBudget.trim() ? `₦${overallBudget.trim()}` : 'Please advise'}`);
    lines.push(`*Delivery address:* ${address.trim()}`);
    lines.push(`*Preferred delivery day:* ${deliveryDay || 'Any day — please advise'}`);
    if (tab === 'photo') {
      lines.push('');
      lines.push('📷 I will attach a photo of my shopping list in this chat now.');
    }
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
  }
  function openWhatsApp(url: string) {
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) window.location.href = url;
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    const order: FieldKey[] = ['name', 'phone', 'market', 'list', 'address'];
    const firstError = order.find(field => nextErrors[field]);
    if (firstError) {
      const node = fieldRefs[firstError].current;
      node?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center'
      });
      window.setTimeout(() => node?.focus({
        preventScroll: true
      }), reduceMotion ? 0 : 350);
      return;
    }
    const url = buildWhatsAppUrl();
    if (tab === 'photo') {
      setShowPhotoToast(true);
      window.setTimeout(() => {
        setShowPhotoToast(false);
        openWhatsApp(url);
      }, 3000);
      return;
    }
    openWhatsApp(url);
  }
  const required = <span className="text-red-500" aria-hidden="true" data-id="element-120" data-mp="207wtl3geg2ow">
      *
    </span>;
  return <section id="order" className="w-full bg-lavender py-14 sm:py-20" data-id="element-121" data-mp="1v8afyswnskfk">
      <div className="mx-auto max-w-3xl px-4 sm:px-6" data-id="element-122" data-mp="1v92bk3q9ai5u">
        <Reveal className="text-center" data-id="element-123" data-mp="2a4rv7v7yyj7e">
          <h2 className="font-display text-3xl uppercase tracking-tight text-purple sm:text-4xl" data-id="element-124" data-mp="2a5t8858jpfrr">
            Start Your Market Run
          </h2>
          <p className="mt-2 text-sm text-ink/65 sm:text-base" data-id="element-125" data-mp="2orqkpkn9n3jh">
            Fill this in and it opens neatly in WhatsApp, ready to send.
          </p>
        </Reveal>

        <div className="mt-6" data-id="element-126" data-mp="2srit9xcnt9ou">
          <NoticeBar compact rounded data-id="element-127" data-mp="2eulwlf9v0y01" />
        </div>

        <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-6 rounded-card bg-white p-5 shadow-lift sm:p-8" data-id="element-128" data-mp="2ew6p6zcm66v9">
          <div className="grid gap-5 sm:grid-cols-2" data-id="element-129" data-mp="254aqbm2pecpz">
            <div data-id="element-130" data-mp="252pvdvch8rrl">
              <label className={labelClass} htmlFor="name" data-id="element-131" data-mp="2526aq6vfzp9v">
                Your name {required}
              </label>
              <input id="name" ref={fieldRefs.name} value={name} onChange={event => {
              setName(event.target.value);
              clearError('name');
            }} placeholder="e.g. Chioma Okeke" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={`${errors.name ? inputErrorClass : inputClass} mt-2`} data-id="element-132" data-mp="1w3kxmr8qp2gh" />
              <FieldError id="name-error" message={errors.name} data-id="element-133" data-mp="1r3q4eh1hs0s9" />
            </div>
            <div data-id="element-134" data-mp="26l7vwr29d28j">
              <label className={labelClass} htmlFor="phone" data-id="element-135" data-mp="26lq14d6weqsl">
                Phone / WhatsApp number {required}
              </label>
              <input id="phone" ref={fieldRefs.phone} type="tel" inputMode="tel" value={phone} onChange={event => {
              setPhone(event.target.value);
              clearError('phone');
            }} placeholder="0803 000 0000" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className={`${errors.phone ? inputErrorClass : inputClass} mt-2`} data-id="element-136" data-mp="1xlkx8d0wyaj6" />
              <FieldError id="phone-error" message={errors.phone} data-id="element-137" data-mp="1ev3f12dpixc2" />
            </div>
          </div>

          <div data-id="element-138" data-mp="19ws8b0ipl2y2">
            <label className={labelClass} htmlFor="market" data-id="element-139" data-mp="19x1qi1xii5t7">
              Choose a market {required}
            </label>
            <select id="market" ref={fieldRefs.market} value={market} onChange={event => {
            setMarket(event.target.value);
            clearError('market');
          }} aria-invalid={Boolean(errors.market)} aria-describedby={errors.market ? 'market-error' : undefined} className={`${errors.market ? inputErrorClass : inputClass} mt-2`} data-id="element-140" data-mp="14wnpv4y8py57">
              <option value="" data-id="element-141" data-mp="zsl8b1wk8cm8">Select a market</option>
              {markets.map(option => <option key={option} value={option} data-id="element-142" data-mp="zqh74rq1prpn">
                  {option}
                </option>)}
            </select>
            <FieldError id="market-error" message={errors.market} data-id="element-143" data-mp="73whbvp7xno7" />
          </div>

          <fieldset ref={fieldRefs.list} tabIndex={-1} className="outline-none" data-id="element-144" data-mp="c2qwmnnz8u3k">
            <legend className={labelClass} data-id="element-145" data-mp="c0mx0hx42h93">Your shopping list {required}</legend>

            <div role="tablist" aria-label="How to send your shopping list" className="mt-2 flex gap-1 rounded-full bg-lavender p-1" data-id="element-146" data-mp="h09beuooe3jh">
              <button type="button" role="tab" aria-selected={tab === 'type'} onClick={() => {
              setTab('type');
              clearError('list');
            }} className={`flex-1 rounded-full px-4 py-2 text-xs font-medium transition sm:text-sm ${tab === 'type' ? 'bg-purple text-white shadow-soft' : 'text-purple'}`} data-id="element-147" data-mp="3kikf381vcfn9">
                Type your list
              </button>
              <button type="button" role="tab" aria-selected={tab === 'photo'} onClick={() => {
              setTab('photo');
              clearError('list');
            }} className={`flex-1 rounded-full px-4 py-2 text-xs font-medium transition sm:text-sm ${tab === 'photo' ? 'bg-purple text-white shadow-soft' : 'text-purple'}`} data-id="element-148" data-mp="v1by4w8rabou">
                Send a photo
              </button>
            </div>

            {tab === 'type' ? <div className="mt-4 space-y-3" data-id="element-149" data-mp="gauwbbs7ljrf">
                <AnimatePresence initial={false} data-id="element-150" data-mp="g91qfypf73kh">
                  {items.map((item, index) => <motion.div key={item.id} layout={!reduceMotion} initial={reduceMotion ? false : {
                opacity: 0,
                y: -12,
                height: 0
              }} animate={{
                opacity: 1,
                y: 0,
                height: 'auto'
              }} exit={reduceMotion ? undefined : {
                opacity: 0,
                y: -8,
                height: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeOut'
              }} className="grid grid-cols-1 gap-2 overflow-hidden sm:grid-cols-[1.4fr,0.8fr,1fr,auto]" data-id="element-151" data-mp="l2cy22223odg" data-mpi={index}>
                    <input aria-label={`Item ${index + 1} name`} value={item.name} onChange={event => updateItem(item.id, 'name', event.target.value)} placeholder="Item name" className={errors.list ? inputErrorClass : inputClass} data-id="element-152" data-mp="336m6z7469rdo" />
                    <input aria-label={`Item ${index + 1} quantity`} value={item.quantity} onChange={event => updateItem(item.id, 'quantity', event.target.value)} placeholder="Quantity" className={inputClass} data-id="element-153" data-mp="2tfhr856oa2hs" />
                    <input aria-label={`Item ${index + 1} budget, optional`} value={item.budget} onChange={event => updateItem(item.id, 'budget', event.target.value)} placeholder="₦ Budget (optional)" className={inputClass} data-id="element-154" data-mp="2fhjq41hd42k8" />
                    <button type="button" onClick={() => removeItem(item.id)} aria-label={`Remove item ${index + 1}`} disabled={items.length === 1} className="flex items-center justify-center rounded-2xl border border-lavender px-3 py-3 text-ink/45 transition hover:text-purple disabled:opacity-40 sm:px-3" data-id="element-155" data-mp="20ul26ghprqa9">
                      <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-156" data-mp="1vwsyyqbmchqr" />
                    </button>
                  </motion.div>)}
                </AnimatePresence>

                <FieldError id="list-error" message={errors.list} data-id="element-157" data-mp="2ok5mb2plstl4" />

                <button type="button" onClick={() => setItems(current => [...current, createItem()])} className="inline-flex items-center gap-2 rounded-full border-2 border-purple px-5 py-2.5 text-sm font-bold text-purple transition duration-200 hover:-translate-y-0.5 hover:bg-lavender hover:shadow-soft active:scale-95" data-id="element-158" data-mp="2omi1j83z8puh">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-159" data-mp="37awvo49lcjzi" />
                  Add another item
                </button>
              </div> : <div className="mt-4 rounded-card border-2 border-dashed border-purple/30 bg-lavender-light px-6 py-8 text-center" data-id="element-160" data-mp="vuwbi1mja4a4">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-purple" data-id="element-161" data-mp="qwl2ft42bxsy">
                  <CameraIcon className="h-7 w-7 text-white" aria-hidden="true" data-id="element-162" data-mp="15nu0mqm16rhp" />
                </span>
                <p className="mt-4 text-sm font-bold text-purple" data-id="element-163" data-mp="10p0iulzpoebq">Have your list written on paper? No problem.</p>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink/70" data-id="element-164" data-mp="7xz1qfcgk8pk">
                  Fill in your details below, and when WhatsApp opens, just attach the photo of your list in the chat.
                </p>
              </div>}
          </fieldset>

          <div data-id="element-165" data-mp="huva8l2cwuu4">
            <label className={labelClass} htmlFor="budget" data-id="element-166" data-mp="hyj2zhzz9d7s">
              Overall budget for the run (optional)
            </label>
            <div className="relative mt-2" data-id="element-167" data-mp="1tbv3pk0fxfpq">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-purple" data-id="element-168" data-mp="1tb23z2zwfqh3">
                ₦
              </span>
              <input id="budget" type="number" min="0" inputMode="numeric" value={overallBudget} onChange={event => setOverallBudget(event.target.value)} placeholder="50,000" className={`${inputClass} pl-9`} data-id="element-169" data-mp="2smbtd65loa94" />
            </div>
            <p className="mt-1.5 text-xs text-ink/55" data-id="element-170" data-mp="32muhwe840exd">
              Leave empty if you set budgets per item or want me to advise.
            </p>
          </div>

          <div data-id="element-171" data-mp="3bl65s8hlcym4">
            <label className={labelClass} htmlFor="address" data-id="element-172" data-mp="3bkwhcpc72zij">
              Delivery address {required}
            </label>
            <textarea id="address" ref={fieldRefs.address} rows={3} value={address} onChange={event => {
            setAddress(event.target.value);
            clearError('address');
          }} placeholder="House number, street, area, landmark" aria-invalid={Boolean(errors.address)} aria-describedby={errors.address ? 'address-error' : undefined} className={`${errors.address ? inputErrorClass : inputClass} mt-2 resize-y`} data-id="element-173" data-mp="3gdg349l1d0yf" />
            <FieldError id="address-error" message={errors.address} data-id="element-174" data-mp="1enib64x9zv6b" />
          </div>

          <div data-id="element-175" data-mp="1epv39awqzlx3">
            <label className={labelClass} htmlFor="delivery-day" data-id="element-176" data-mp="19ofeb7rg8v9f">
              Preferred delivery day (optional)
            </label>
            <input id="delivery-day" type="date" value={deliveryDay} onChange={event => setDeliveryDay(event.target.value)} className={`${inputClass} mt-2`} data-id="element-177" data-mp="14qe0b18vrm4t" />
            <p className="mt-1.5 text-xs text-ink/55" data-id="element-178" data-mp="1xe0161qq4cnz">Orders before 12pm qualify for next-day delivery.</p>
          </div>

          <div data-id="element-179" data-mp="1smrp0xy0ed74">
            <button type="submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-purple px-6 py-4 text-sm font-bold text-white shadow-soft transition duration-200 hover:-translate-y-0.5 hover:bg-purple-soft hover:shadow-lift active:translate-y-0 active:scale-[0.99] sm:text-base" data-id="element-180" data-mp="1sm89tozqu2ws">
              <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-181" data-mp="2gc2lw4c8nnfz" />
              Send My List on WhatsApp
            </button>
            <p className="mt-3 text-center text-xs leading-relaxed text-ink/60" data-id="element-182" data-mp="eg3p8c7vddsi">
              Your order opens in WhatsApp, fully arranged, so we can confirm cost and payment. Minimum order ₦30,000.
            </p>
            <p className="mt-1 text-center text-xs text-ink/45" data-id="element-183" data-mp="4no8fi8b6v97">
              <span className="text-red-500" data-id="element-184" data-mp="9n0va2z6k2bs">*</span> Required fields
            </p>
          </div>
        </form>
      </div>

      <AnimatePresence data-id="element-185" data-mp="3rwb4fihwwmux">
        {showPhotoToast && <motion.div role="status" aria-live="polite" initial={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-sm rounded-card bg-purple-deep p-4 text-white shadow-lift sm:bottom-8" data-id="element-186" data-mp="3vw23sali8bb2">
            <div className="flex items-start gap-3" data-id="element-187" data-mp="35muoo7woavlj">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366]" data-id="element-188" data-mp="35jgjyt0jeaax">
                <MessageCircleIcon className="h-5 w-5 text-white" aria-hidden="true" data-id="element-189" data-mp="3fbmcc3upatqn" />
              </span>
              <p className="text-sm font-medium leading-relaxed" data-id="element-190" data-mp="3al5wye7bky1n">
                WhatsApp is opening. Remember to attach the photo of your list 📷
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}